import React from 'react'
import Player, { PlayerMountOptions } from '../npo-start-player-web/src/player'
import './app.scss'
import Storage from '../npo-start-player-web/src/storage/storage'
import Tabs from './components/tabs/Tabs'
import AppTabTheme from './tabs/theme/AppTabTheme'
import AppTabSource from './tabs/source/AppTabSource'
import AppTabExport from './tabs/export/AppTabExport'
import AppTabPlayback from './tabs/playback/AppTabPlayback'
import { Subject, Subscription } from 'rxjs'
import Playback from '../npo-start-player-web/src/playback/playback'

/**
 * The state
 */
interface AppState {
	/**
	 * The selected theme index
	 */
	selectedThemeIndex: number
}

/**
 * The main app entry point.
 * @author Stan Hurks
 */
export default class App extends React.Component<any, AppState> {

	/**
	 * Re-initialize the player
	 */
	public static reInitializePlayer: Subject<void> = new Subject()

	/**
	 * The player
	 */
	public static player: Player | null

	/**
     * The subscription to the `Playback.volumeChanged` subject.
     */
	private subscriptionPlaybackVolumeChanged!: Subscription

	/**
	 * The subscription to the `App.reInitializePlayer` subject.
	 */
	private subscriptionReInitializePlayer!: Subscription

	constructor(props: any) {
		super(props)

		this.state = {
			selectedThemeIndex: 0
		}
	}

	public render = () => {
		return (
			<div className="app">
				<div className="app-player">
					<div className="app-player-aspect-ratio">
						<div id="npo-player"></div>
					</div>
				</div>
				<div className="app-bottom">
					<Tabs tabs={[
						{
							name: 'Theme',
							content: (<AppTabTheme />)
						},
						{
							name: 'Playback',
							content: (<AppTabPlayback />)
						},
						{
							name: 'Source',
							content: (<AppTabSource />)
						},
						{
							name: 'Export',
							content: (<AppTabExport />)
						}
					]} />
				</div>
			</div>
		)
	}

	public componentDidMount = () => {
		if (App.player) {
			Player.destroy.next(App.player.id)
		}

		// Wait for player to destroy
		setTimeout(() => {

			// Wait for utility classes to destroy
			setTimeout(() => {
				this.initializePlayer({
					container: document.getElementById('npo-player')!,
					web: {
						keys: {
							player: 'D634EA2C-BFE7-4BB6-9EAA-CDCC7B1EDFFA',
							analytics: 'ba1840c5-dd3e-4232-8197-6d452876278b'
						}
					},
					theme: {
						colors: {
							primary: Storage.data.theme.colorPrimary,
							secondary: Storage.data.theme.colorSecondary
						},
						logo: Storage.data.theme.hasLogo
							? {
								src: Storage.data.theme.logoSrc,
								url: Storage.data.theme.logoURL,
								fadeOutAtMs: Storage.data.theme.logoFadeOutAtMs,
								fadeOutDurationMs: Storage.data.theme.logoFadeOutDurationMs
							}
							: null,
						autoHideControls: Storage.data.theme.autoHideControls
					},
					playback: {
						autoplay: Storage.data.playback.autoplay,
						volume: Storage.data.playback.volume
					}
				}).then(() => {
					App.player!.load({
						title: Storage.data.source.title,
						description: Storage.data.source.description,
						protocol: Storage.data.source.protocol,
						prid: Storage.data.source.prid,
						thumbnailTrack: {
							url: Storage.data.source.thumbnailTrackURL
						},
						poster: Storage.data.source.poster,
						subtitleTracks: [
							{
								id: 'nl',
								label: 'Nederlands',
								lang: 'nl-nl',
								kind: 'subtitle',
								url: Storage.data.source.subtitleURL
							}
						]
					})
				})
				this.subscriptionPlaybackVolumeChanged = Playback.volumeChanged.subscribe((volume) => {
					Storage.data.playback.volume = Math.round(volume)
				})
				this.subscriptionReInitializePlayer = App.reInitializePlayer.subscribe(() => {
					this.componentWillUnmount()
		
					this.componentDidMount()
				})
			})
		})
	}

	public componentWillUnmount = () => {
		this.subscriptionPlaybackVolumeChanged.unsubscribe()
		this.subscriptionReInitializePlayer.unsubscribe()
	}

	/**
	 * Initializes the player
	 */
	private initializePlayer = (playerMountOptions: PlayerMountOptions): Promise<void> => {
		return new Promise((resolve) => {
			App.player = new Player(playerMountOptions)
			resolve()
		})
	}
}